import React from "react"
import { Col, Modal, Row, Spinner } from "react-bootstrap"

export interface PromiseResultSuccess<S> {
  data: S
}

export interface PromiseResultError {
  error: unknown
}

export type PendingResultType<S> = "NotStarted" | "Pending" | PromiseResultSuccess<S> | PromiseResultError

export interface ResultRenderer<S> {
  onPending: () => string
  onSuccess: (data: S) => string
  onError: (error: unknown) => string
  showResultTimeMs?: number
}

export interface PendingModalProps<S> {
  pendingResult: PendingResultType<S>
  resultRenderer: ResultRenderer<S>
  size?: "sm" | "lg" | "xl"
}

export const PendingModal = <S, E>(props: PendingModalProps<S>) => {
  const { pendingResult: result, resultRenderer } = props

  const show = result !== "NotStarted"

  const { data, error } = result as any

  const heading =
    result === "Pending"
      ? resultRenderer.onPending()
      : data
      ? resultRenderer.onSuccess(data)
      : error
      ? resultRenderer.onError(error)
      : ""
  

  return (
    <Modal
      show={show}
      size={props.size}
      backdrop="static"
      keyboard={false}
      scrollable={true}
      dialogClassName="shadow-5"
    >
      <Modal.Header>
        <Modal.Title>{heading}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="d-flex flex-fill justify-content-center">
          <Col sm={12} md={8} xl={5} className="my-auto text-center">
            {result === "Pending" ? (
              <Spinner animation="border" variant="primary" />
            ) : data ? (
              <i className="modus-icons" aria-hidden="true">
                check
              </i>
            ) : error ? (
              <i className="modus-icons" aria-hidden="true">
                alert
              </i>
            ) : (
              ""
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer />
    </Modal>
  )
}

export const getStartPending = <S,>(
  getData: () => Promise<S>,
  setPendingModalProps: (props: PendingModalProps<S>) => void,
  resultRenderer: ResultRenderer<S>
) => {
  return async () => {
    setPendingModalProps({ pendingResult: "Pending", resultRenderer })
    try {
      const data = await getData()
      setPendingModalProps({ pendingResult: { data }, resultRenderer })
      await sleep(resultRenderer.showResultTimeMs || 1000)
    } catch (error) {
      setPendingModalProps({ pendingResult: { error }, resultRenderer })
      await sleep(resultRenderer.showResultTimeMs || 1000)
    }
    setPendingModalProps({ pendingResult: "NotStarted", resultRenderer })
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
